import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import TopNavBar from '../usercomponents/TopNavBar';
import Footer from '../usercomponents/Footer';
import SideBar from '../usercomponents/SideBar';
import { AreaChart, formatINR } from '../admincomponents/DashboardCharts';
import '../manage/AdminDashboard.css';
import './StudentDashboard.css';

const apiUrl = process.env.REACT_APP_API_URL;
const SUPPORT_PHONE = '+91 90486 48777';
const SUPPORT_TEL = 'tel:+919048648777';
const SUPPORT_WHATSAPP = 'https://wa.me/919048648777';

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d.length === 10 ? `${d}T00:00:00` : d);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const dueText = (a) => {
  if (a.overdue) return 'Overdue';
  if (a.daysLeft === null) return 'No deadline';
  if (a.daysLeft <= 0) return 'Due today';
  if (a.daysLeft === 1) return 'Due tomorrow';
  return `Due in ${a.daysLeft} days`;
};

function ProgressRing({ percent = 0, size = 112, stroke = 11, color = '#fff', track = 'rgba(255,255,255,0.22)', children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, percent));
  return (
    <div className="sd-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${(p / 100) * c} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="sd-ring-center">{children}</div>
    </div>
  );
}

function Panel({ title, subtitle, action, children, className = '' }) {
  return (
    <section className={`dash-panel ${className}`}>
      <header className="dash-panel-head">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

// A friendly "nothing here yet" block - always used instead of an error or a bare zero.
function Empty({ icon, title, text, action }) {
  return (
    <div className="sd-empty">
      <span className="sd-empty-icon"><i className={`ph ${icon}`} /></span>
      <strong>{title}</strong>
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}

function Tile({ to, icon, tone, label, value, sub, bar, children }) {
  const body = (
    <>
      <div className="sd-tile-top">
        <span className="sd-tile-icon"><i className={`ph ${icon}`} /></span>
        <span className="sd-tile-label">{label}</span>
      </div>
      <div className="sd-tile-value">{value}</div>
      {bar != null && <div className="sd-tile-bar"><div style={{ width: `${bar}%` }} /></div>}
      {sub && <div className="sd-tile-sub">{sub}</div>}
      {children}
    </>
  );
  return to ? <Link to={to} className={`sd-tile dash-tone-${tone}`}>{body}</Link> : <div className={`sd-tile dash-tone-${tone}`}>{body}</div>;
}

function SkeletonBody() {
  return (
    <div aria-busy="true" aria-label="Loading your dashboard">
      <div className="sd-skel-row">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="dash-sk sd-skel-tile" />)}</div>
      <div className="dash-grid-2"><div className="dash-sk sd-skel-block" /><div className="dash-sk sd-skel-block" /></div>
    </div>
  );
}

const QUICK_LINKS = [
  { to: '/courses', icon: 'ph-graduation-cap', label: 'My courses' },
  { to: '/student-resources', icon: 'ph-books', label: 'Study material' },
  { to: '/mock-exams', icon: 'ph-exam', label: 'Mock exams' },
  { to: '/assignments', icon: 'ph-file-text', label: 'Assignments' },
  { to: '/fees', icon: 'ph-coins', label: 'Fees' },
];

function Dashboard() {
  const session = JSON.parse(sessionStorage.getItem('studentSession') || 'null') || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      if (!session.token) { setFailed(true); return; }
      const res = await axios.get(`${apiUrl}/api/student/dashboard-summary`, {
        params: { nocache: Date.now() },
        headers: { Authorization: `Bearer ${session.token}` },
      });
      if (res.data && res.data.success) setData(res.data);
      else setFailed(true);
    } catch (e) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const firstName = (data?.student?.firstName || session.firstName || '').trim();
  const enrolments = data?.enrolments || [];
  const enrolled = enrolments.length > 0;
  const onboarding = data?.onboarding;
  const progress = data?.progress || { totalLessons: 0, readLessons: 0, percent: 0 };
  const withNext = enrolments.find((e) => e.nextLesson);
  const hasAnyLessons = enrolments.some((e) => e.totalLessons > 0);

  // ---------- Hero ----------
  let heroSub;
  if (!data) heroSub = 'Your learning space at IFM Edu Academy.';
  else if (!enrolled) heroSub = "Welcome to IFM Edu Academy! You aren't enrolled in a course yet.";
  else if (onboarding?.show) heroSub = "Let's get you started. A few quick steps will help you make the most of your course.";
  else if (progress.percent >= 80) heroSub = 'You are almost through your lessons. Keep up the great work!';
  else if (progress.percent >= 40) heroSub = "You're making steady progress. Keep the momentum going!";
  else heroSub = 'Every lesson you finish gets you closer to your certificate.';

  let heroCard = null;
  if (data) {
    if (withNext) {
      heroCard = (
        <div className="sd-continue">
          <span className="sd-continue-label"><i className="ph ph-play-circle" /> Continue learning</span>
          <strong>{withNext.nextLesson.title}</strong>
          <em>{withNext.course}{withNext.nextLesson.date ? ` · ${formatDate(withNext.nextLesson.date)}` : ''}</em>
          <Link to={`/course-details/${withNext.batchId}`} className="sd-btn">Resume lesson <i className="ph ph-arrow-right" /></Link>
        </div>
      );
    } else if (enrolled && !hasAnyLessons) {
      const rolling = enrolments.find((e) => e.rolling);
      heroCard = (
        <div className="sd-continue">
          <span className="sd-continue-label"><i className="ph ph-hourglass-medium" /> Your first lesson is on its way</span>
          <strong>Lessons appear here as soon as your trainer uploads them.</strong>
          <em>{rolling ? `Your classes are shown from the day you joined (${formatDate(rolling.joined)}).` : 'Check back soon, or open your course to see what is available.'}</em>
          <Link to={`/course-details/${enrolments[0].batchId}`} className="sd-btn">Open my course <i className="ph ph-arrow-right" /></Link>
        </div>
      );
    } else if (enrolled) {
      heroCard = (
        <div className="sd-continue">
          <span className="sd-continue-label"><i className="ph ph-confetti" /> You're all caught up</span>
          <strong>You have finished every lesson that's available right now.</strong>
          <em>New lessons will show up here. In the meantime, test yourself with a mock exam.</em>
          <Link to="/mock-exams" className="sd-btn">Try a mock exam <i className="ph ph-arrow-right" /></Link>
        </div>
      );
    } else {
      heroCard = (
        <div className="sd-continue">
          <span className="sd-continue-label"><i className="ph ph-rocket-launch" /> Start your journey</span>
          <strong>Once you are enrolled, your lessons, exams and certificates will appear here.</strong>
          <em>Questions about courses or enrolment? We're happy to help.</em>
          <div className="sd-btn-row">
            <a href="https://ifmedu.in" target="_blank" rel="noopener noreferrer" className="sd-btn">Explore courses <i className="ph ph-arrow-right" /></a>
            <a href={SUPPORT_WHATSAPP} target="_blank" rel="noopener noreferrer" className="sd-btn sd-btn-ghost">Chat with us</a>
          </div>
        </div>
      );
    }
  }

  // ---------- To-do ----------
  const todo = [];
  if (data) {
    (data.assignments?.items || []).filter((a) => !a.submitted)
      .sort((a, b) => (Number(b.overdue) - Number(a.overdue)) || ((a.daysLeft ?? 9999) - (b.daysLeft ?? 9999)))
      .forEach((a) => todo.push({
        key: `a${a.id}`, to: `/assignments/${a.id}`, icon: 'ph-file-text', tone: a.overdue ? 'red' : 'orange',
        title: a.title, meta: `${a.course} · ${dueText(a)}`, badge: a.overdue ? 'Overdue' : 'Assignment',
      }));
    (data.mock?.inProgress || []).forEach((p) => todo.push({
      key: `p${p.attemptId}`, to: `/mock-exam-take/${p.attemptId}`, icon: 'ph-timer', tone: 'purple',
      title: p.title, meta: 'You started this exam, pick up where you left off', badge: 'Resume',
    }));
    (data.mock?.available || []).filter((e) => !e.resumeAttemptId && e.done === 0).slice(0, 2).forEach((e) => todo.push({
      key: `e${e.id}`, to: `/mock-exam-instructions/${e.id}`, icon: 'ph-exam', tone: 'purple',
      title: e.title, meta: `${e.course}${e.minutes ? ` · ${e.minutes} min` : ''}`, badge: 'Mock exam',
    }));
    if (enrolled && !data.attendance?.markedToday) todo.push({
      key: 'att', to: '/mark-attendance', icon: 'ph-calendar-check', tone: 'teal',
      title: "Mark today's attendance", meta: 'Scan the QR code shown in class', badge: 'Today',
    });
  }

  const reviewed = (data?.assignments?.items || []).filter((a) => a.reviewed && a.marks !== null).slice(0, 3);
  const mock = data?.mock;
  const fees = data?.fees;
  const nextDue = (data?.assignments?.items || []).filter((a) => !a.submitted && a.daysLeft !== null)
    .sort((a, b) => a.daysLeft - b.daysLeft)[0];

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="dash sdash">
            {/* ---------- Hero ---------- */}
            <section className="sd-hero">
              <div className="sd-hero-main">
                <p className="sd-eyebrow">{greeting()}</p>
                <h1>
                  Hi, {firstName || 'there'}{' '}
                  <img src="/assets/images/icons/wave-hand.png" alt="" />
                </h1>
                <p className="sd-hero-sub">{heroSub}</p>
                {loading && !data && <div className="dash-sk sd-hero-skel" />}
                {heroCard}
              </div>

              {data && enrolled && (
                <aside className="sd-hero-side">
                  {onboarding?.show ? (
                    <>
                      <div className="sd-side-head">
                        <ProgressRing percent={(onboarding.done / onboarding.total) * 100} size={84} stroke={9}>
                          <strong>{onboarding.done}/{onboarding.total}</strong>
                        </ProgressRing>
                        <div>
                          <h3>Getting started</h3>
                          <p>Your first steps</p>
                        </div>
                      </div>
                      <ul className="sd-steps">
                        {onboarding.steps.map((s) => (
                          <li key={s.key} className={s.done ? 'done' : ''}>
                            <Link to={s.href}>
                              <i className={`ph ${s.done ? 'ph-check-circle' : 'ph-circle'}`} />
                              <span>{s.label}</span>
                              {!s.done && <i className="ph ph-caret-right sd-step-go" />}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <div className="sd-side-progress">
                      <ProgressRing percent={progress.percent} size={128} stroke={12}>
                        <strong>{progress.percent}%</strong>
                        <span>complete</span>
                      </ProgressRing>
                      <p>
                        {progress.totalLessons > 0
                          ? `${progress.readLessons} of ${progress.totalLessons} lessons completed`
                          : 'Your progress will appear once lessons are available'}
                      </p>
                    </div>
                  )}
                </aside>
              )}
              {data && !enrolled && (
                <aside className="sd-hero-side sd-hero-help">
                  <i className="ph ph-headset" />
                  <h3>Need a hand?</h3>
                  <p>Our team will help you pick the right course and get enrolled.</p>
                  <a href={SUPPORT_TEL} className="sd-btn sd-btn-ghost">{SUPPORT_PHONE}</a>
                </aside>
              )}
            </section>

            {/* ---------- Soft notice if the data couldn't be fetched (never a scary error) ---------- */}
            {failed && (
              <div className="sd-notice">
                <span><i className="ph ph-cloud-slash" /> We couldn't refresh your dashboard just now. Your courses and lessons are safe.</span>
                <button type="button" onClick={load} disabled={loading}>{loading ? 'Trying…' : 'Try again'}</button>
              </div>
            )}
            {data?.partial && !failed && (
              <div className="sd-notice sd-notice-soft"><span><i className="ph ph-info" /> A few sections are still loading. Refresh the page in a moment to see everything.</span></div>
            )}

            {failed && !data && (
              <Panel title="Jump back in" subtitle="Everything you need is one tap away">
                <div className="dash-actions sd-links">
                  {QUICK_LINKS.map((a) => (
                    <Link key={a.to} to={a.to} className="dash-action"><i className={`ph ${a.icon}`} /><span>{a.label}</span></Link>
                  ))}
                </div>
              </Panel>
            )}

            {loading && !data && !failed && <SkeletonBody />}

            {data && (
              <div className={loading ? 'dash-content dash-refreshing' : 'dash-content'}>
                {/* ---------- Tiles ---------- */}
                <div className="sd-tiles">
                  <Tile
                    to={enrolled ? `/course-details/${enrolments[0].batchId}` : '/courses'} icon="ph-books" tone="teal" label="Lessons completed"
                    value={progress.totalLessons > 0 ? `${progress.readLessons}/${progress.totalLessons}` : '—'}
                    bar={progress.totalLessons > 0 ? progress.percent : null}
                    sub={progress.totalLessons > 0 ? `${progress.percent}% of your lessons` : 'Lessons will appear here soon'}
                  />
                  <Tile
                    to="/student-my-attendance" icon="ph-calendar-check" tone="blue" label="Attendance"
                    value={data.attendance.daysThisMonth}
                    sub={data.attendance.markedToday ? 'days this month · marked today ✓' : enrolled ? 'days this month' : 'Starts once you are enrolled'}
                  >
                    {enrolled && !data.attendance.markedToday && <span className="sd-tile-link">Mark today's attendance →</span>}
                  </Tile>
                  <Tile
                    to="/mock-exams" icon="ph-exam" tone="purple" label="Mock exam average"
                    value={mock.average != null ? `${mock.average}%` : '—'}
                    sub={mock.completed > 0 ? `${mock.completed} attempt${mock.completed === 1 ? '' : 's'} · best ${mock.best}%` : 'No attempts yet. Give one a try!'}
                  />
                  <Tile
                    to="/assignments" icon="ph-file-text" tone="orange" label="Assignments to do"
                    value={data.assignments.pending}
                    sub={data.assignments.items.length === 0 ? 'None assigned yet' : data.assignments.pending === 0 ? 'All submitted ✓' : data.assignments.overdue > 0 ? `${data.assignments.overdue} overdue, please submit soon` : nextDue ? `Next: ${dueText(nextDue).toLowerCase()}` : 'Open assignments'}
                  />
                  <Tile
                    to="/fees" icon="ph-coins" tone="green" label="Fees"
                    value={fees.percentPaid != null ? `${fees.percentPaid}% paid` : '—'}
                    bar={fees.percentPaid}
                    sub={fees.billed <= 0 ? 'No fees recorded' : fees.due > 0 ? `${formatINR(fees.due)} due of ${formatINR(fees.billed)}` : 'Fully paid ✓'}
                  />
                </div>

                {/* ---------- My courses ---------- */}
                <Panel title="My courses" subtitle="Your progress in each course">
                  {!enrolled ? (
                    <Empty
                      icon="ph-graduation-cap" title="No courses yet"
                      text="When you are enrolled, your courses and lesson progress will show up here."
                      action={<a href="https://ifmedu.in" target="_blank" rel="noopener noreferrer" className="sd-btn sd-btn-solid">Explore courses</a>}
                    />
                  ) : (
                    <div className="sd-courses">
                      {enrolments.map((e) => (
                        <Link key={e.batchId} to={`/course-details/${e.batchId}`} className="sd-course">
                          <div className="sd-course-img" style={e.image ? { backgroundImage: `url(${apiUrl}/${e.image})` } : undefined}>
                            {!e.image && <i className="ph ph-graduation-cap" />}
                          </div>
                          <div className="sd-course-body">
                            <strong>{e.course}</strong>
                            <em>{e.batch}</em>
                            {e.totalLessons > 0 ? (
                              <>
                                <div className="sd-bar"><div style={{ width: `${e.percent}%` }} /></div>
                                <span className="sd-course-meta">{e.readLessons} of {e.totalLessons} lessons · {e.percent}%</span>
                              </>
                            ) : (
                              <span className="sd-course-meta sd-course-wait"><i className="ph ph-hourglass-medium" /> {e.rolling ? 'Lessons appear from your join date' : 'Lessons coming soon'}</span>
                            )}
                          </div>
                          <i className="ph ph-caret-right sd-course-go" />
                        </Link>
                      ))}
                    </div>
                  )}
                </Panel>

                {/* ---------- To-do + performance ---------- */}
                <div className="dash-grid-2 sd-grid-even">
                  <Panel title="Up next" subtitle="Things worth doing today">
                    {todo.length === 0 ? (
                      <Empty
                        icon={enrolled ? 'ph-check-circle' : 'ph-list-checks'}
                        title={enrolled ? "You're all caught up" : 'Your to-do list is empty'}
                        text={enrolled ? 'Nothing is due right now. Enjoy the free time!' : 'Once you are enrolled, your assignments, exams and reminders will show up here.'}
                      />
                    ) : (
                      <ul className="dash-list">
                        {todo.slice(0, 6).map((t) => (
                          <li key={t.key}>
                            <Link to={t.to} className="dash-row">
                              <span className={`sd-row-icon dash-tone-${t.tone}`}><i className={`ph ${t.icon}`} /></span>
                              <span className="dash-row-main"><strong>{t.title}</strong><em>{t.meta}</em></span>
                              <span className={`sd-badge sd-badge-${t.tone}`}>{t.badge}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>

                  <Panel
                    title="My performance" subtitle="Your mock exam scores"
                    action={mock.completed > 0 ? <Link to="/mock-exam-history" className="dash-link">History</Link> : null}
                  >
                    {mock.trend.length >= 2 ? (
                      <AreaChart
                        data={mock.trend.map((t) => ({ ...t, year: '' }))} height={190} color="#7a5af8"
                        formatValue={(v) => `${v}%`} formatTick={(v) => `${v}%`} valueLabel="score"
                      />
                    ) : mock.trend.length === 1 ? (
                      <div className="sd-single-score">
                        <ProgressRing percent={mock.trend[0].value} size={104} stroke={10} color="#7a5af8" track="#eeeaff">
                          <strong style={{ color: '#1c2733' }}>{mock.trend[0].value}%</strong>
                        </ProgressRing>
                        <p>Your first exam score. Take another to start tracking your progress over time.</p>
                      </div>
                    ) : (
                      <Empty
                        icon="ph-chart-line-up" title="Your score trend will appear here"
                        text="Take your first mock exam to see how you're improving."
                        action={<Link to="/mock-exams" className="sd-btn sd-btn-solid">Browse mock exams</Link>}
                      />
                    )}
                    {reviewed.length > 0 && (
                      <div className="sd-marks">
                        <h4>Recent assignment marks</h4>
                        <ul className="dash-list">
                          {reviewed.map((a) => (
                            <li key={a.id}>
                              <Link to={`/assignments/${a.id}`} className="dash-row">
                                <span className="sd-row-icon dash-tone-green"><i className="ph ph-seal-check" /></span>
                                <span className="dash-row-main"><strong>{a.title}</strong><em>{a.comment ? a.comment : a.course}</em></span>
                                <span className="sd-score">{a.marks}{a.maxMarks ? `/${a.maxMarks}` : ''}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Panel>
                </div>

                {/* ---------- Certificates + materials ---------- */}
                <div className="dash-grid-2 sd-grid-even">
                  <Panel title="My certificates" subtitle="Download or share your achievements">
                    {data.certificates.length === 0 ? (
                      <Empty icon="ph-certificate" title="Your certificate is waiting" text="Complete your course and your certificate will appear here, ready to download." />
                    ) : (
                      <ul className="dash-list">
                        {data.certificates.map((c) => (
                          <li key={c.id}>
                            <div className="dash-row sd-cert">
                              <span className="sd-row-icon dash-tone-gold"><i className="ph ph-certificate" /></span>
                              <span className="dash-row-main"><strong>{c.course || 'Certificate'}</strong><em>No. {c.number} · issued {formatDate(c.issued)}</em></span>
                              {c.filePath && (
                                <a href={`${apiUrl}/${c.filePath}`} target="_blank" rel="noopener noreferrer" className="sd-btn sd-btn-small">Download</a>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>

                  <Panel title="New for you" subtitle="Latest study material and case studies" action={<Link to="/student-resources" className="dash-link">All resources</Link>}>
                    {data.materials.length === 0 ? (
                      <Empty icon="ph-books" title="Study material is on the way" text="New material and case studies for your course will show up here." />
                    ) : (
                      <ul className="dash-list">
                        {data.materials.map((m) => (
                          <li key={`${m.type}${m.id}`}>
                            <Link to={m.type === 'case-study' ? '/student-case-studies' : '/student-resources'} className="dash-row">
                              <span className={`sd-row-icon ${m.type === 'case-study' ? 'dash-tone-orange' : 'dash-tone-blue'}`}><i className={`ph ${m.type === 'case-study' ? 'ph-briefcase' : 'ph-file-pdf'}`} /></span>
                              <span className="dash-row-main"><strong>{m.title}</strong><em>{m.course}</em></span>
                              <span className="dash-row-meta">{m.type === 'case-study' ? 'Case study' : 'Material'}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>
                </div>

                {/* ---------- Help ---------- */}
                <div className="sd-help">
                  <div>
                    <strong>Need help with anything?</strong>
                    <span>Our team is happy to help with lessons, exams or fees.</span>
                  </div>
                  <div className="sd-btn-row">
                    <a href={SUPPORT_TEL} className="sd-btn sd-btn-small sd-btn-solid"><i className="ph ph-phone" /> {SUPPORT_PHONE}</a>
                    <a href={SUPPORT_WHATSAPP} target="_blank" rel="noopener noreferrer" className="sd-btn sd-btn-small sd-btn-outline"><i className="ph ph-whatsapp-logo" /> WhatsApp</a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default Dashboard;
